import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Heart, Mail, Loader2, ArrowRight } from 'lucide-react';
import Logo from '../Logo';

export default function CharityAuth({ onLogin }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [devToken, setDevToken] = useState(null);

  const handleRequestLink = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/charity-portal/auth/request-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      setLinkSent(true);
      toast.success('Check your email for the login link!');
      
      // For development, show the token
      if (data.dev_token) {
        setDevToken(data.dev_token);
      }
    } catch (error) {
      toast.error('Failed to send login link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDevLogin = async () => {
    if (!devToken) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/charity-portal/auth/verify-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: devToken })
      });

      if (response.ok) {
        const data = await response.json();
        onLogin(data.session_token, data.charity);
        toast.success('Welcome!');
      } else {
        toast.error('Login failed');
      }
    } catch (error) {
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <Logo size="large" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="w-6 h-6 text-emerald-600 fill-emerald-600" />
            <CardTitle className="text-2xl font-bold text-gray-700">
              DealShaq Charity App
            </CardTitle>
          </div>
          <CardDescription className="text-base">
            Access your donation dashboard and payment settings
          </CardDescription>
        </CardHeader>

        <CardContent>
          {!linkSent ? (
            <form onSubmit={handleRequestLink} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@charity.org"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="charity-email-input"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Enter the email associated with your charity account
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
                data-testid="charity-request-link-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Login Link
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-gray-500 mt-4">
                No password needed! We'll send you a secure login link.
              </p>
              
              <div className="border-t mt-6 pt-4">
                <p className="text-center text-sm text-gray-600">
                  Not a partner yet?{' '}
                  <a href="/charity/apply" className="text-emerald-600 hover:text-emerald-700 font-medium">
                    Apply to join DealShaq
                  </a>
                </p>
              </div>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Check Your Email</h3>
              <p className="text-gray-600">
                We've sent a login link to <strong>{email}</strong>
              </p>
              <p className="text-sm text-gray-500">
                The link will expire in 24 hours.
              </p>

              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setLinkSent(false);
                  setDevToken(null);
                }}
              >
                Use a different email
              </Button>

              {/* Dev mode quick login */}
              {devToken && (
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800 mb-2 font-medium">
                    🔧 Development Mode
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={handleDevLogin}
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Quick Login (Dev)'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
